import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { BoardSort } from '@obraplan/core/domain/planning/weekly-board';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { HoursProgress } from '@/components/ui/hours-progress';
import { EmptyState } from '@/components/feedback/states';
import { cn } from '@/lib/cn';
import { DayTotalCell, HoursCell, QuantityCell } from './planner-cells';
import { formatTotalQuantity } from './planner-format';
import { alternar, groupBoardByTeam, teamOfCell } from './planner-teams';
import type { WeeklyPlannerGridProps } from './WeeklyPlannerGrid';
import { TeamCard, TeamHeldMark } from './WeeklyPlannerTeams';

/**
 * A semana no celular.
 *
 * A grade de sete colunas com o nome do recurso fixo à esquerda funciona no monitor e no
 * tablet deitado. Num telefone ela vira 1.138px atrás de uma rolagem horizontal: para
 * lançar quinta-feira é preciso arrastar até lá, e ao chegar o nome de quem se está
 * planejando já saiu da tela.
 *
 * Aqui a mesma semana é uma **lista de cartões, um por pessoa**. Dentro do cartão, cada
 * frente traz os dias em grade de quatro colunas — dois toques de polegar, com o dia
 * escrito em cima de cada campo. O nome nunca sai da vista porque ele é o cabeçalho do
 * cartão, não uma coluna.
 *
 * As células são **as mesmas** da grade (`planner-cells`): o aviso de excesso, a
 * granularidade de 15 minutos e o gesto de "campo vazio remove" não têm segunda versão.
 *
 * Na obra por equipe (Fase 30, D10), o agrupamento também é o da grade (`planner-teams`): um
 * cartão por equipe, com as horas somadas no dia, que abre nas pessoas.
 */

const SORT_OPTIONS: readonly { id: BoardSort; label: string }[] = [
  { id: 'BALANCE', label: 'Saldo' },
  { id: 'NAME', label: 'Nome' },
  { id: 'TEAM', label: 'Equipe' },
];

export function WeeklyPlannerCards({
  board,
  sort,
  onSortChange,
  readOnly,
  canPlan,
  onCreate,
  onEdit,
  onDelete,
  onAdd,
  onAddTeam,
}: WeeklyPlannerGridProps) {
  const editable = !readOnly && canPlan;
  // D10 — o mesmo agrupamento da grade: um cartão por equipe, que abre nas pessoas.
  const agrupado = useMemo(() => groupBoardByTeam(board), [board]);
  const nomesDasEquipes = useMemo(
    () => new Map(board.teams.map((team) => [team.teamId as string, team.name])),
    [board.teams],
  );
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());

  if (board.rows.length === 0) {
    return (
      <EmptyState
        title="Nenhum recurso cadastrado"
        description="Cadastre funcionários ou serviços terceirizados na tela de Mão de obra para poder planejar a semana."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1 overflow-x-auto">
        <span className="shrink-0 text-xs font-medium text-(--color-content-muted)">Ordenar</span>
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={sort === option.id}
            onClick={() => onSortChange(option.id)}
            className={cn(
              'min-h-11 shrink-0 rounded-md px-3 text-xs',
              sort === option.id
                ? 'bg-(--color-surface-muted) font-medium text-(--color-content)'
                : 'text-(--color-content-muted)',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {agrupado.teams.map((group) => (
        <TeamCard
          key={group.team.teamId}
          group={group}
          days={board.days}
          editable={editable}
          expanded={abertas.has(group.team.teamId)}
          onToggle={() => setAbertas((atual) => alternar(atual, group.team.teamId))}
          onEdit={onEdit}
          onDelete={onDelete}
          onAdd={onAdd}
          {...(onAddTeam === undefined ? {} : { onAddTeam })}
        />
      ))}

      {agrupado.rows.map((row) => (
        <section
          key={row.resource.resourceId}
          className="flex flex-col gap-3 rounded-(--radius-card) border border-(--color-border-subtle) p-3"
        >
          <header className="flex flex-col gap-2">
            <div>
              <p className="font-medium">{row.resource.name}</p>
              <p className="text-xs text-(--color-content-muted)">
                {row.resource.detail}
                {row.resource.teamName !== undefined && ` · ${row.resource.teamName}`}
              </p>
              {!row.resource.isAllocatable && (
                <p className="mt-1 text-xs text-(--color-state-warn)">
                  Inativo — não recebe nova alocação
                </p>
              )}
            </div>
            <HoursProgress balance={row.balance} />
          </header>

          {row.activities.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">Sem alocação nesta semana.</p>
          ) : (
            row.activities.map((activityRow) => (
              <div
                key={activityRow.activity.activityId}
                className="flex flex-col gap-2 border-t border-(--color-border-subtle) pt-3"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{activityRow.activity.label}</p>
                    <p className="truncate text-xs text-(--color-content-muted)">
                      {activityRow.activity.towerName}
                    </p>
                  </div>
                  <span className="tabular shrink-0 text-sm">
                    {Hours.ofQuarters(activityRow.plannedQuarterHours).format()}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-1.5">
                  {activityRow.cells.map((cell, index) => {
                    const day = board.days[index]!;
                    const dayTotal = row.dayTotals[index]!;
                    const daEquipe = teamOfCell(cell);
                    if (daEquipe !== undefined) {
                      return (
                        <div key={cell.date} className="flex flex-col items-center gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-(--color-content-muted)">
                            {day.weekdayLabel}
                          </span>
                          <TeamHeldMark
                            cell={cell}
                            teamName={nomesDasEquipes.get(daEquipe) ?? 'Equipe'}
                          />
                        </div>
                      );
                    }
                    return (
                      <label
                        key={cell.date}
                        className={cn(
                          'flex flex-col items-center gap-0.5',
                          !day.isWorkday && 'opacity-60',
                        )}
                      >
                        <span className="text-[10px] uppercase tracking-wide text-(--color-content-muted)">
                          {day.weekdayLabel}
                        </span>
                        <HoursCell
                          quarterHours={cell.allocation?.quarterHours ?? 0}
                          dayTotal={dayTotal}
                          isWorkday={day.isWorkday}
                          exceedsCapacity={cell.allocation?.exceedsCapacity ?? false}
                          justification={cell.allocation?.justification}
                          resourceName={row.resource.name}
                          activityLabel={activityRow.activity.label}
                          dayLabel={`${day.weekdayLabel} ${day.shortLabel}`}
                          editable={editable}
                          onCommit={(quarterHours) => {
                            if (cell.allocation === undefined) {
                              onCreate({
                                resourceId: row.resource.resourceId,
                                activityId: activityRow.activity.activityId,
                                date: cell.date,
                                quarterHours,
                              });
                              return;
                            }
                            if (quarterHours === 0) {
                              onDelete({
                                allocationId: cell.allocation.id,
                                quarterHours: cell.allocation.quarterHours,
                                version: cell.allocation.version,
                              });
                              return;
                            }
                            onEdit({
                              allocationId: cell.allocation.id,
                              quarterHours,
                              version: cell.allocation.version,
                            });
                          }}
                        />
                      </label>
                    );
                  })}
                </div>

                {/* A meta só aparece onde a frente é medida — igual à grade. */}
                {activityRow.activity.unitName !== undefined && (
                  <details className="text-sm">
                    <summary className="min-h-11 cursor-pointer py-2 text-xs text-(--color-content-muted)">
                      Meta em {activityRow.activity.unitName} ·{' '}
                      {formatTotalQuantity(
                        {
                          cells: activityRow.cells.filter((cell) => teamOfCell(cell) === undefined),
                        },
                        activityRow.activity.unitDecimals ?? 2,
                      )}{' '}
                      na semana
                    </summary>
                    <div className="mt-1 grid grid-cols-4 gap-1.5">
                      {activityRow.cells.map((cell, index) =>
                        // A meta do dia da equipe está no cartão dela.
                        teamOfCell(cell) !== undefined ? (
                          <span
                            key={cell.date}
                            className="flex items-center justify-center text-xs text-(--color-content-muted)"
                          >
                            —
                          </span>
                        ) : (
                          <label key={cell.date} className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] uppercase tracking-wide text-(--color-content-muted)">
                              {board.days[index]!.weekdayLabel}
                            </span>
                            <QuantityCell
                              plannedQuantity={cell.allocation?.plannedQuantity}
                              executedQuantity={cell.allocation?.executedQuantity}
                              unitName={activityRow.activity.unitName!}
                              decimals={activityRow.activity.unitDecimals ?? 2}
                              resourceName={row.resource.name}
                              activityLabel={activityRow.activity.label}
                              dayLabel={`${board.days[index]!.weekdayLabel} ${board.days[index]!.shortLabel}`}
                              editable={editable && cell.allocation !== undefined}
                              onCommit={(plannedQuantity) => {
                                if (cell.allocation === undefined) return;
                                onEdit({
                                  allocationId: cell.allocation.id,
                                  quarterHours: cell.allocation.quarterHours,
                                  version: cell.allocation.version,
                                  plannedQuantity,
                                });
                              }}
                            />
                          </label>
                        ),
                      )}
                    </div>
                  </details>
                )}
              </div>
            ))
          )}

          {/* Total do dia — é aqui que o excesso de jornada aparece (RN-044). */}
          {row.activities.length > 0 && (
            <div className="border-t border-(--color-border-subtle) pt-2">
              <p className="mb-1 text-[10px] uppercase tracking-wide text-(--color-content-muted)">
                Total do dia
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {row.dayTotals.map((dayTotal, index) => (
                  <div key={dayTotal.date} className="flex flex-col items-center">
                    <span className="text-[10px] uppercase text-(--color-content-muted)">
                      {board.days[index]!.weekdayLabel}
                    </span>
                    <DayTotalCell total={dayTotal} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {editable && row.resource.isAllocatable && (
            <button
              type="button"
              onClick={() => onAdd(row.resource.resourceId)}
              className="inline-flex min-h-11 items-center justify-center gap-1 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 text-sm"
            >
              <Plus className="size-4" aria-hidden="true" />
              Adicionar atividade
            </button>
          )}
        </section>
      ))}
    </div>
  );
}
